import { Button, Form } from 'antd';
import ModalCustom from '../../utils/ModalCustom/ModalCustom';
import { useState } from 'react';
import { useTranslation } from '../../../i18n/i18n';
import { EditOutlined } from '@ant-design/icons';
import FormBuildingData, { InstallationLocationBuildingFormValues } from './FormBuildingData';

interface EditBuildingProps extends InstallationLocationBuildingFormValues {
  onSave: (values: InstallationLocationBuildingFormValues) => Promise<void>;
}

const clearNaN = (values: InstallationLocationBuildingFormValues): InstallationLocationBuildingFormValues =>
  Object.fromEntries(
    Object.entries(values).map(([key, value]) => [
      key,
      typeof value === 'number' && Number.isNaN(value) ? undefined : value,
    ]),
  ) as InstallationLocationBuildingFormValues;

const EditBuilding = ({ onSave, ...buildingValues }: EditBuildingProps) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { t } = useTranslation('installation-location', { keyPrefix: 'installationLocationDetail' });
  const [form] = Form.useForm<InstallationLocationBuildingFormValues>();

  const handleOpen = () => {
    form.setFieldsValue(clearNaN(buildingValues));
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    let rawValues: InstallationLocationBuildingFormValues;
    try {
      rawValues = await form.validateFields();
    } catch {
      return;
    }
    try {
      setIsLoading(true);
      await onSave(rawValues);
      setIsModalOpen(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button onClick={handleOpen} icon={<EditOutlined />}>
        {t('editBuilding')}
      </Button>
      <ModalCustom
        title={t('editBuilding')}
        description={t('editBuildingDesc')}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        footer={
          <Button type={'primary'} onClick={handleSave} loading={isLoading}>
            {t('save')}
          </Button>
        }
      >
        <Form
          form={form}
          layout="vertical"
          className={'Modal__form'}
          onFinish={handleSave}
          onKeyDown={(e) => {
            const target = e.target as HTMLElement;
            if (e.key === 'Enter' && target.tagName !== 'TEXTAREA' && !e.defaultPrevented) {
              e.preventDefault();
              form.submit();
            }
          }}
        >
          <FormBuildingData />
        </Form>
      </ModalCustom>
    </>
  );
};

export default EditBuilding;
